import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificacionesAPI, personasAPI, semanasConfirmadasAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { format, addWeeks, startOfWeek, isBefore } from 'date-fns';
import { es } from 'date-fns/locale';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Convierte @[Nombre](id) → @Nombre en el texto de preview */
const cleanMentions = (text) =>
  text ? text.replace(/@\[([^\]]+)\]\(\d+\)/g, '@$1') : '';

const formatFecha = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Estilos ────────────────────────────────────────────────────────────────

const overlay = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(44,62,80,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  padding: '16px',
  backdropFilter: 'blur(2px)',
};

const modal = {
  background: '#fff',
  borderRadius: '12px',
  width: '100%',
  maxWidth: '660px',
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 16px 48px rgba(44,62,80,0.22)',
  overflow: 'hidden',
  border: '1px solid #e9ecef',
};

const header = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '16px',
  padding: '22px 26px 18px',
  borderBottom: '3px solid #dc3545',
  background: '#fff',
};

const bellWrap = {
  width: '48px',
  height: '48px',
  borderRadius: '10px',
  background: '#dc3545',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const listArea = {
  overflowY: 'auto',
  padding: '12px 20px',
  flex: 1,
};

const itemCard = {
  display: 'flex',
  gap: '14px',
  padding: '14px 16px',
  borderRadius: '8px',
  marginBottom: '10px',
  border: '1px solid #e9ecef',
  background: '#f8f9fa',
};

const previewBox = {
  fontStyle: 'italic',
  color: '#495057',
  background: '#fff',
  border: '1px solid #dee2e6',
  borderRadius: '6px',
  padding: '8px 12px',
  fontSize: '13px',
  lineHeight: '1.5',
  marginTop: '8px',
  maxHeight: '60px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const footer = {
  display: 'flex',
  gap: '10px',
  padding: '14px 26px',
  borderTop: '1px solid #e9ecef',
  background: '#f8f9fa',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  alignItems: 'center',
};

const btnPrimary = {
  background: '#dc3545',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '9px 18px',
  fontSize: '13px',
  fontWeight: '600',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const btnOutline = {
  background: '#fff',
  color: '#dc3545',
  border: '1.5px solid #dc3545',
  borderRadius: '6px',
  padding: '8px 14px',
  fontSize: '13px',
  fontWeight: '500',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const btnGhost = {
  background: 'transparent',
  color: '#6c757d',
  border: '1.5px solid #dee2e6',
  borderRadius: '6px',
  padding: '8px 14px',
  fontSize: '13px',
  fontWeight: '500',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const badgeTipo = (tipo) => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '20px',
  fontSize: '11px',
  fontWeight: '600',
  marginBottom: '4px',
  background: '#fdecea',
  color: '#c82333',
  border: '1px solid #f5c6cb',
});

const itemCardSecundario = {
  display: 'flex',
  gap: '14px',
  padding: '10px 14px',
  borderRadius: '8px',
  marginBottom: '8px',
  border: '1px solid #ffeeba',
  background: '#fffdf0',
};

const badgeTarea = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: '20px',
  fontSize: '11px',
  fontWeight: '600',
  marginBottom: '3px',
  background: '#fff3cd',
  color: '#856404',
  border: '1px solid #ffc107',
};

const btnTarea = {
  background: '#fff',
  color: '#856404',
  border: '1.5px solid #ffc107',
  borderRadius: '6px',
  padding: '7px 13px',
  fontSize: '13px',
  fontWeight: '500',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

// ─── Componente principal ───────────────────────────────────────────────────

// IDs de notificaciones ya mostradas en el modal durante esta sesión de pestaña.
// Se persiste en sessionStorage para sobrevivir recargas, pero se borra en login/logout.
const SHOWN_IDS_KEY = 'notif_shown_ids';

const getShownIds = () => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SHOWN_IDS_KEY) || '[]'));
  } catch { return new Set(); }
};

const addShownIds = (ids) => {
  const current = getShownIds();
  ids.forEach((id) => current.add(id));
  sessionStorage.setItem(SHOWN_IDS_KEY, JSON.stringify([...current]));
};

const NotificacionesModal = () => {
  const { sessionId, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const [visible, setVisible] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const [tareasPendientes, setTareasPendientes] = useState([]);
  const [cargando, setCargando] = useState(false);

  const cerrar = useCallback(() => setVisible(false), []);

  // Carga tareas de horas pendientes
  const cargarTareas = useCallback(async () => {
    try {
      let persona = user?.persona;
      if (!persona) {
        const res = await personasAPI.getCurrentUser();
        persona = res.data.persona;
      }
      if (!persona?.id) return [];

      const confirmedWeekKeys = await semanasConfirmadasAPI.getConfirmedWeekKeys(persona.id);
      const confirmedSet = new Set(confirmedWeekKeys);

      const start = startOfWeek(new Date(2025, 11, 1), { weekStartsOn: 1 });
      const today = new Date();
      const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });

      const pending = [];
      let w = start;
      while (isBefore(w, currentWeekStart) || format(w, 'yyyy-MM-dd') === format(currentWeekStart, 'yyyy-MM-dd')) {
        const key = `${persona.id}-${format(w, 'yyyy-MM-dd')}`;
        if (!confirmedSet.has(key)) {
          pending.push({
            weekStart: new Date(w),
            weekParam: format(w, 'yyyy-MM-dd'),
            label: `Semana del ${format(w, "d 'de' MMMM yyyy", { locale: es })}`,
          });
        }
        w = addWeeks(w, 1);
      }
      return pending.sort((a, b) => a.weekStart - b.weekStart);
    } catch {
      return [];
    }
  }, [user]);

  // Se ejecuta en cada carga/recarga de página (isAuthenticated: false→true)
  // y también cuando cambia sessionId (nuevo login).
  // Muestra el modal sólo para notificaciones no leídas que NO se hayan
  // mostrado ya en este historial de sesión de pestaña.
  useEffect(() => {
    if (!isAuthenticated || !sessionId) return;

    setCargando(true);

    Promise.all([
      notificacionesAPI.getMisNotificaciones().catch(() => ({ data: [] })),
      cargarTareas(),
    ]).then(([resNotif, tareas]) => {
      const noLeidas = (resNotif.data || []).filter((n) => !n.leida);
      const shownIds = getShownIds();
      const nuevas = noLeidas.filter((n) => !shownIds.has(n.id));

      if (nuevas.length > 0) {
        addShownIds(nuevas.map((n) => n.id));
        setNotificaciones(nuevas);
        setTareasPendientes(tareas);
        setVisible(true);
      } else if (tareas.length > 0) {
        // Solo tareas, sin menciones nuevas: mostrar igual pero sin bloquear
        // Usamos una clave de sesión para no repetir en la misma sesión
        const tareasKey = 'notif_tareas_shown_' + sessionId;
        if (!sessionStorage.getItem(tareasKey)) {
          sessionStorage.setItem(tareasKey, '1');
          setNotificaciones([]);
          setTareasPendientes(tareas);
          setVisible(true);
        }
      }
    }).finally(() => setCargando(false));
  }, [sessionId, isAuthenticated, cargarTareas]);

  const marcarUna = useCallback(async (id) => {
    try { await notificacionesAPI.marcarLeida(id); } catch { /* silencioso */ }
    setNotificaciones((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const marcarTodas = useCallback(async () => {
    try { await notificacionesAPI.marcarTodasLeidas(); } catch { /* silencioso */ }
    cerrar();
  }, [cerrar]);

  const irA = useCallback(async (notif) => {
    try { await notificacionesAPI.marcarLeida(notif.id); } catch { /* silencioso */ }
    const restantes = notificaciones.filter((n) => n.id !== notif.id);
    setNotificaciones(restantes);
    if (restantes.length === 0 && tareasPendientes.length === 0) cerrar();
    if (notif.iniciativa) navigate(`/projects/${notif.iniciativa}`);
    else navigate('/desarrollo-negocio');
  }, [notificaciones, tareasPendientes.length, cerrar, navigate]);

  const irATarea = useCallback((tarea) => {
    cerrar();
    navigate(`/time-tracking?week=${tarea.weekParam}`);
  }, [cerrar, navigate]);

  // Si tras marcar quedan 0 menciones Y 0 tareas, cerrar
  useEffect(() => {
    if (visible && notificaciones.length === 0 && tareasPendientes.length === 0 && !cargando) cerrar();
  }, [visible, notificaciones.length, tareasPendientes.length, cargando, cerrar]);

  if (!visible) return null;

  const countMenciones = notificaciones.length;
  const countTareas = tareasPendientes.length;

  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label="Notificaciones pendientes">
      <div style={modal}>

        {/* ── Cabecera ── */}
        <div style={header}>
          <div style={bellWrap}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
          </div>
          <div>
            <h2 style={{ margin: 0, color: '#2c3e50', fontSize: '20px', fontWeight: '700' }}>
              {countMenciones > 0
                ? (countMenciones === 1 ? 'Tienes 1 mención pendiente de leer' : `Tienes ${countMenciones} menciones pendientes de leer`)
                : `Tienes ${countTareas} tarea${countTareas !== 1 ? 's' : ''} pendiente${countTareas !== 1 ? 's' : ''} de horas`}
            </h2>
            <p style={{ margin: '5px 0 0', color: '#6c757d', fontSize: '14px', lineHeight: '1.4' }}>
              {countMenciones > 0 && countTareas > 0
                ? `Además tienes ${countTareas} semana${countTareas !== 1 ? 's' : ''} de horas sin confirmar.`
                : countMenciones > 0
                ? 'Alguien te ha mencionado en comentarios. Revísalos para no perder ninguna novedad.'
                : 'Hay semanas con horas sin confirmar. Revisa el registro de tiempo.'}
            </p>
          </div>
        </div>

        {/* ── Lista menciones (prioritario) ── */}
        <div style={listArea}>
          {countMenciones > 0 && (
            <>
              {countTareas > 0 && (
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#dc3545', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>
                  ★ Menciones — prioritario
                </div>
              )}
              {notificaciones.map((notif) => (
                <div key={notif.id} style={itemCard}>
                  {/* Icono lateral */}
                  <div style={{ paddingTop: '2px', flexShrink: 0 }}>
                    <div style={{
                      width: '34px', height: '34px', borderRadius: '50%',
                      background: '#fdecea',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '15px',
                    }}>
                      {notif.tipo === 'mencion_iniciativa' ? '📋' : '📣'}
                    </div>
                  </div>

                  {/* Contenido */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={badgeTipo(notif.tipo)}>
                      {notif.tipo === 'mencion_iniciativa' ? 'Iniciativa' : 'Acción DN'}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', marginBottom: '2px' }}>
                      {notif.iniciativa_nombre || notif.accion_dn_nombre || '—'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      <strong style={{ color: '#495057' }}>{notif.autor_nombre}</strong>
                      {' te ha mencionado · '}
                      {formatFecha(notif.fecha_creacion)}
                    </div>
                    {notif.texto_preview && (
                      <div style={previewBox}>
                        "{cleanMentions(notif.texto_preview)}"
                      </div>
                    )}
                  </div>

                  {/* Acciones */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0, alignSelf: 'center' }}>
                    <button style={btnPrimary} onClick={() => irA(notif)} title="Ir al contexto y marcar como leída">
                      Ver →
                    </button>
                    <button style={btnOutline} onClick={() => marcarUna(notif.id)} title="Marcar como leída sin navegar">
                      ✓ Leída
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* ── Separador + Tareas pendientes (secundario) ── */}
          {countTareas > 0 && (
            <>
              {countMenciones > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '16px 0 12px' }}>
                  <div style={{ flex: 1, height: '1px', background: '#dee2e6' }} />
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                    Tareas pendientes
                  </span>
                  <div style={{ flex: 1, height: '1px', background: '#dee2e6' }} />
                </div>
              )}
              {!countMenciones && (
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#856404', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>
                  ⏰ Horas sin confirmar
                </div>
              )}
              {tareasPendientes.slice(0, 5).map((tarea) => (
                <div key={tarea.weekParam} style={itemCardSecundario}>
                  <div style={{ paddingTop: '2px', flexShrink: 0 }}>
                    <div style={{
                      width: '34px', height: '34px', borderRadius: '50%',
                      background: '#fff3cd',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '15px',
                    }}>
                      ⏰
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={badgeTarea}>
                      Horas sin confirmar
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                      {tarea.label}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, alignSelf: 'center' }}>
                    <button style={btnTarea} onClick={() => irATarea(tarea)}>
                      Completar
                    </button>
                  </div>
                </div>
              ))}
              {countTareas > 5 && (
                <div style={{ textAlign: 'center', padding: '6px 0 4px', fontSize: '12px', color: '#6c757d' }}>
                  y {countTareas - 5} semanas más sin confirmar
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Pie ── */}
        <div style={footer}>
          <span style={{ color: '#6c757d', fontSize: '13px', alignSelf: 'center', marginRight: 'auto' }}>
            {countMenciones > 0 && `${countMenciones} mención${countMenciones !== 1 ? 'es' : ''}`}
            {countMenciones > 0 && countTareas > 0 && ' · '}
            {countTareas > 0 && `${countTareas} semana${countTareas !== 1 ? 's' : ''} sin confirmar`}
          </span>
          <button style={btnGhost} onClick={cerrar}>
            Cerrar
          </button>
          {countMenciones > 0 && (
            <button style={btnPrimary} onClick={marcarTodas}>
              ✓ Marcar todas como leídas
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default NotificacionesModal;
